import { vendorColor } from "../vendorColors";
import { VENDOR_LOGO_PATHS } from "../components/vendorLogos";

/** Vendor display name → Simple Icons slug. Vendors without an
    available official mark resolve to undefined and fall back to the
    letter avatar. */
const VENDOR_NAME_TO_SLUG: Record<string, string> = {
  OpenAI: "openai",
  Anthropic: "anthropic",
  Google: "google",
  "Google Gemini": "google",
  DeepSeek: "deepseek",
  Alibaba: "alibabacloud",
  Qwen: "qwen",
  "Alibaba DashScope": "alibabacloud",
  "Baidu Qianfan": "baidu",
  "Baidu Qianfan v2": "baidu",
  ByteDance: "bytedance",
  Baidu: "baidu",
  "Moonshot AI": "moonshotai",
  Moonshot: "moonshotai",
  Kuaishou: "kuaishou",
  MiniMax: "minimax",
  Mistral: "mistralai",
  "Fish Audio": "fishaudio",
  Perplexity: "perplexity",
  Ollama: "ollama",
};

export function vendorLogoSlug(vendor: string): string | undefined {
  return VENDOR_NAME_TO_SLUG[vendor];
}

/**
 * Official vendor mark tinted with the vendor's brand colour, with a
 * tinted letter avatar as the fallback for vendors whose mark is not
 * available. Used by the model tables, the filter rail, and the
 * landing-page provider strip.
 */
export function VendorLogo({
  vendor,
  size = 28,
  mono,
}: {
  vendor: string;
  size?: number;
  /** Force a single glyph colour (e.g. "#fff") instead of the brand tint. */
  mono?: string;
}) {
  const slug = VENDOR_NAME_TO_SLUG[vendor];
  const paths = slug ? VENDOR_LOGO_PATHS[slug] : undefined;

  if (!paths) {
    // Letter fallback keeps the tinted-box idiom so unknown vendors stay
    // visually distinct from brands whose mark we render.
    const color = vendorColor(vendor);
    return (
      <span
        className="mk-avatar"
        aria-label={vendor}
        style={{
          width: size,
          height: size,
          fontSize: Math.round(size * 0.4),
          borderColor: `color-mix(in srgb, ${color} 40%, var(--as-line))`,
          background: `color-mix(in srgb, ${color} 14%, var(--as-surface-raised))`,
          color,
        }}
      >
        {vendor ? vendor.charAt(0).toUpperCase() : "?"}
      </span>
    );
  }

  const glyph = Math.round(size * 0.68);
  return (
    <span
      className="mk-vlogo"
      role="img"
      aria-label={vendor}
      style={{ width: size, height: size, color: mono ?? vendorColor(vendor) }}
    >
      <svg viewBox="0 0 24 24" width={glyph} height={glyph} fill="currentColor" aria-hidden="true">
        {paths.map((d, i) => (
          <path key={i} d={d} />
        ))}
      </svg>
    </span>
  );
}
